import KPIViewTableService, {
  KPITableView
} from '@modules/order/stores/view/KPI/KPIViewTableService'
import { nav } from '@stores/nav'
import PaginatedTableHierarchyItemsUIStore from '@stores/paginated-table-hierarchy-items/PaginatedTableHierarchyItemsUIStore'
import map from 'lodash/map'
import maxBy from 'lodash/maxBy'
import omit from 'lodash/omit'
import values from 'lodash/values'
import { action, computed, observable } from 'mobx'
import { KPIDataStore } from './KPIDataStore'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getEnumValue } from '@utils/get-enum-value'
import { CATEGORY_INTERNAL_SEPERATOR } from '@components/UI-Components/HierarchyWiseItems/constants'
import { HIERARCHY_ROOT_ID } from '@modules/order/constants/uiConstants'
import EnhancedKPIViewTableService from './EnhancedKPIViewTableService'
import { computedFn } from 'mobx-utils'
import { find } from 'lodash'

export class KPIUIStore extends PaginatedTableHierarchyItemsUIStore<KPIDataStore> {
  @observable isKPISettingsOpen = false
  @observable openCustomViewSettingsPopup = false
  private isRetailClient =
    nav.queryParams.clientType === getEnumValue(VIPClientType.Internal)

  tempAvaialbleViews = this.isRetailClient
    ? nav.queryParams.subSection || KPITableView.Buy
    : nav.queryParams.subSection || KPITableView.Overview
  tempSettingsByViews = this.isRetailClient
    ? [KPITableView.Buy, KPITableView.Forecast]
    : [KPITableView.Overview, KPITableView.DoorsKPI]

  tableStore = this.isEnhancedViewEnabled
    ? new EnhancedKPIViewTableService(
        this.tempAvaialbleViews,
        this.tempSettingsByViews,
        {}
      )
    : new KPIViewTableService(this.tempAvaialbleViews, this.tempSettingsByViews, {})

  @computed
  get hierarchyDetails() {
    if (!this.dataStore.heirarchy?.length) {
      return {
        [`${HIERARCHY_ROOT_ID}`]: {
          children: [],
          itemHeight: 0,
          startIndex: null,
          endIndex: null
        }
      }
    }
    // omit finished key from heirarchy details
    const heirarchy: any = omit(
      this.generateTableDataRecursivelyForKPI(this.dataStore.heirarchy, '', 0, 0),
      'finished'
    )

    return {
      [HIERARCHY_ROOT_ID]: {
        children: map(this.dataStore.heirarchy, 'name')
      },
      ...heirarchy
    }
  }

  getPlanningBudgetHeirarchy = computedFn(
    (heirarchyGroups: string, doorId?: string): any => {
      const doorKPIs = {}
      const heirarchyGroupsIds = heirarchyGroups?.split(CATEGORY_INTERNAL_SEPERATOR)
      const firstLevel = doorId ? doorId : heirarchyGroupsIds[0]
      this.getHeirarchyRecursively(
        find(
          this.dataStore?.planningBudgetHeirarchy,
          doorOrder => doorOrder?.id === firstLevel
        ),
        heirarchyGroupsIds,
        doorKPIs
      )
      return doorKPIs?.[heirarchyGroupsIds.pop()]
    }
  )

  getDoorWiseHierarchyKPIs = computedFn(
    (doorId: string, heirarchyGroups: string): any => {
      const doorKPIs = {}
      const heirarchyGroupsIds = heirarchyGroups?.split(CATEGORY_INTERNAL_SEPERATOR)
      const doorOrders =
        find(this.dataStore?.totalDoorKPIs, door => door?.id === doorId)?.children || {}
      const doorOrderKPIs = find(
        doorOrders,
        doorOrder => doorOrder?.id === heirarchyGroupsIds[0]
      )
      this.getHeirarchyRecursively(doorOrderKPIs, heirarchyGroupsIds, doorKPIs)
      return doorKPIs?.[heirarchyGroupsIds.pop()]
    }
  )

  getHeirarchyRecursively = (doorOrderKPIs, heirarchyGroupsIds, doorKPIs) => {
    heirarchyGroupsIds.forEach((activity, index) => {
      const heirarchyLevelArr = heirarchyGroupsIds.slice(0, index + 1)
      if (
        activity === doorOrderKPIs?.id &&
        heirarchyLevelArr.every(level => doorOrderKPIs?.parentGroupNames?.includes(level))
      ) {
        doorKPIs[activity] = doorOrderKPIs
        return
      } else {
        if (doorOrderKPIs?.children) {
          doorOrderKPIs?.children.forEach(doorOrder => {
            this.getHeirarchyRecursively(doorOrder, heirarchyGroupsIds, doorKPIs)
          })
        }
      }
    })
  }

  private generateTableDataRecursivelyForKPI(children, groupName, finished, depth) {
    return (children || []).reduce(
      (acc, child) => {
        let res = {}
        if (child?.children?.length) {
          const result = this.generateTableDataRecursivelyForKPI(
            child.children,
            `${groupName ? `${groupName}${CATEGORY_INTERNAL_SEPERATOR}` : ''}${
              child.name
            }`,
            acc.finished + 1,
            groupName ? depth + 1 : depth
          )
          res = {
            ...result,
            ...(groupName
              ? {
                  [groupName]: {
                    name: groupName,
                    isLeaf: false,
                    depth: depth,
                    children: ((acc[groupName] || {}).children || []).concat([
                      `${groupName}${CATEGORY_INTERNAL_SEPERATOR}${child.name}`
                    ]),
                    startIndex: acc.finished,
                    endIndex: maxBy(values(res), (val: any) =>
                      val.name.includes(groupName) ? val.endIndex : 0
                    )
                  }
                }
              : {
                  [child.name]: {
                    name: child.name,
                    isLeaf: false,
                    depth: depth,
                    children: ((acc[groupName] || {}).children || child.children).map(
                      groupChild =>
                        `${child.name}${CATEGORY_INTERNAL_SEPERATOR}${groupChild.name}`
                    ),
                    startIndex: acc.finished,
                    endIndex: maxBy(values(res), (val: any) =>
                      val.name.includes(groupName) ? val.endIndex : 0
                    )
                  }
                })
          }
        } else {
          res = {
            [groupName]: {
              name: groupName,
              isLeaf: false,
              children: ((acc[groupName] || {}).children || []).concat([
                `${groupName}${CATEGORY_INTERNAL_SEPERATOR}${child.name}`
              ]),
              depth
            },
            [`${groupName}${CATEGORY_INTERNAL_SEPERATOR}${child.name}`]: {
              name: `${groupName}${CATEGORY_INTERNAL_SEPERATOR}${child.name}`,
              isLeaf: true,
              depth: depth + 1
            },
            ...res
          }
        }
        return { ...acc, ...res }
      },
      { finished }
    )
  }

  @action
  setIsKPISettingsOpen = isKPISettingsOpen => {
    this.isKPISettingsOpen = isKPISettingsOpen
  }

  @action setOpenCustomViewSettingsPopup = value => {
    this.openCustomViewSettingsPopup = value
  }
}
